import { useLocation, useNavigate, useNavigation } from "react-router-dom";
import { debounce, isRnRUser } from "../utils/helpers";
import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { GET_OR_UPDATE_DATA_COLLECTION_PROGRESS } from "../constants/URLS";
import { httpClient } from "../http-client";
import { useMissingEntitiesStore } from "../stores/missing-entities-store";
import { useWebQueryParamStore } from "../stores/query-param-store";
import { isNBLocationEnabled } from "../../modules/about-me/utils/about-me.helpers";

interface UpdateRouteType {
    routeUrl?: string;
    getLastVisitedRoute?: boolean;
}

const updateDataCollectionProgress = (routeData: UpdateRouteType) => {
    const { routeUrl = "", getLastVisitedRoute = false } = routeData;
    const isNBNudgeEnabled = useWebQueryParamStore.getState().isNBNudgeEnabled
    if (getLastVisitedRoute) {
        return httpClient
            .get(GET_OR_UPDATE_DATA_COLLECTION_PROGRESS, {
                params: {
                    version: isRnRUser() ? "v1" : "v0",
                    location: isNBLocationEnabled() && (isNBNudgeEnabled || false),
                },
            })
            .then((data) => data.data);
    }
    return httpClient
        .put(GET_OR_UPDATE_DATA_COLLECTION_PROGRESS, {
            last_open_page_id: routeUrl,
            version: isRnRUser() ? "v1": "v0"
        })
        .then((data) => data.data);
};

const useUpdateOrGetCurrentRouteOnServer = () => {
    return useMutation({
        mutationKey: ["data-collection-progress"],
        mutationFn: (data: UpdateRouteType) => updateDataCollectionProgress(data),
    });
};

const useUpdateCurrentRouteOnServer = () => {
    const { pathname = "" } = useLocation();
    const {setMissingEntityDetails} = useMissingEntitiesStore((state) => state);
    const navigate = useNavigate();
    const { mutate: updateCurrentRouteOnServer, ...rest } =
        useUpdateOrGetCurrentRouteOnServer();
    const updateCurrentRoute = debounce(
        () =>
            updateCurrentRouteOnServer({
                routeUrl: pathname,
            }),
        300
    );

    const getLastVisitedRoute = async () =>
        updateCurrentRouteOnServer(
            {
                routeUrl: pathname,
                getLastVisitedRoute: true,
            },
            {
                onSuccess: (data) => {
                    /**
                     * In case it's a RnR user and BE has mentioned that this flow
                     * if to supply the missing-entities, redirect to the missing-entities page.
                     */
                    if (isRnRUser() && Object.keys(data?.missing_entity_page_details || {}).length > 0) {
                        /**
                         * Using a zustand store for storing missinge ntities, as retrieving
                         * these entities from react query will require correct queryKeys everytime
                         * and that might get tricky conidering the nature of this hook.
                         */
                        setMissingEntityDetails(data?.missing_entity_page_details);
                        navigate("/missing-entities");
                        return
                    }

                    if (isRnRUser() && data?.last_open_page_id === "/experience/1") {
                        navigate("/experience");
                        return;
                    }

                    if (isRnRUser() && data?.last_open_page_id === "/web-view-congratulations") {
                        navigate("/exit");
                        return
                    }

                    if (data?.last_open_page_id === "/success") {
                        navigate("/about-me/1");
                        return;
                    }

                    if (data?.last_open_page_id) {
                        navigate(data?.last_open_page_id);
                        return;
                    }

                    if (isRnRUser()) {
                        navigate("/about-me/1");
                    }
                },
                /**
                 * Sometimes, this being an initial call might fail, and in case of failure for RnR users
                 * we exit the web-view. This will make sure that web-view doesn't exit and user lands on
                 * default /about-me/1 page
                 */
                onError: () => {
                    if(isRnRUser()){
                        navigate("/about-me/1");
                    }
                }
            }
        );
    return { updateCurrentRoute, getLastVisitedRoute, ...rest };
};

export default useUpdateCurrentRouteOnServer;
